/**
 * Validación semántica del BPIN — espejo del helper backend
 * `validate_bpin_semantic` (Sprint 11 follow-up #5).
 *
 * **Por qué duplicar la regla en frontend:** queremos feedback inline
 * antes de submit. Si el usuario tipea 14 dígitos pero el año es 2019
 * o el DANE es de otro muni, mostramos el error en rojo bajo el input
 * sin esperar al roundtrip.
 *
 * **Single source of truth:** el backend sigue siendo la autoridad.
 * Si la regla cambia, ambos se actualizan. Tests aseguran paridad.
 */

const MIN_YEAR = 2020;

export interface BpinValidationResult {
  /** True si el BPIN pasa formato (14 dígitos) + año + DANE. */
  valid: boolean;
  /**
   * Mensaje en español listo para mostrar bajo el input. `null` cuando
   * el BPIN está vacío o cuando es válido.
   */
  reason: string | null;
}

/**
 * Valida el BPIN contra el DANE del tenant activo.
 *
 * - Si `bpin` está vacío (o no llega a 14 dígitos): `valid=false` pero
 *   sin razón (no mostramos error mientras escribe).
 * - Año fuera de [2020, currentYear+1]: error claro.
 * - DANE no coincide con el del tenant: error claro.
 *
 * @param bpin Cadena con 0 o más dígitos (sin espacios; el caller debe
 *             haber hecho `.replace(/\D/g, "")`).
 * @param tenantDane DANE de 5 dígitos del muni activo.
 * @param now Fecha actual (default `new Date()`). Inyectable para tests.
 */
export function validateBpin(
  bpin: string,
  tenantDane: string,
  now: Date = new Date(),
): BpinValidationResult {
  if (bpin.length === 0) {
    return { valid: false, reason: null };
  }
  if (bpin.length < 14) {
    // Aún escribiendo — no consideramos error mostrable.
    return { valid: false, reason: null };
  }
  if (bpin.length > 14) {
    return {
      valid: false,
      reason: "El BPIN debe tener exactamente 14 dígitos.",
    };
  }

  const yearStr = bpin.slice(0, 4);
  const year = Number(yearStr);
  const maxYear = now.getFullYear() + 1;
  if (!Number.isFinite(year) || year < MIN_YEAR || year > maxYear) {
    return {
      valid: false,
      reason: `El año del BPIN debe estar entre ${MIN_YEAR} y ${maxYear} (recibido: ${yearStr}).`,
    };
  }

  const bpinDane = bpin.slice(4, 9);
  if (bpinDane !== tenantDane) {
    return {
      valid: false,
      reason: `El código DANE del BPIN (${bpinDane}) no coincide con el de tu municipio (${tenantDane}). Verifica que registraste el BPIN del proyecto correcto.`,
    };
  }

  return { valid: true, reason: null };
}
